import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Settings as SystemIcon,
  Notifications as NotificationIcon,
  Edit as ComposeIcon,
  Folder as FoldersIcon,
  EventBusy as OutOfOfficeIcon,
  AccountCircle as MailAccountsIcon,
  Draw as SignatureIcon,
  Shield as SpamShieldIcon,
  Label as LabelsIcon,
  FilterList as FiltersIcon,
  ImportExport as ImportExportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Keyboard as KeyboardIcon,
  Extension as ExtensionIcon,
  FormatListBulleted as MailViewIcon,
  Tune as PresetIcon,
  Code as DeveloperIcon,
  Hub as ChannelsIcon,
  DesktopWindows as DesktopNotifIcon,
  PriorityHigh as PriorityIcon,
  DoNotDisturb as QuietModeIcon,
  Email as MailNotifIcon,
  Palette as AppearanceIcon,
  VpnKey as AppPasswordsIcon,
} from '@mui/icons-material';
import { useSettingsStore } from '../../stores/settingsStore';
import { useThemeStore } from '../../stores/themeStore';
import { syncFromBackendSettings } from '../../services/notificationService';

interface SettingsMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  children?: SettingsMenuItem[];
}

interface SettingsSection {
  title: string;
  items: SettingsMenuItem[];
}

const SettingsLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const { settings, fetchSettings } = useSettingsStore();
  const { setMode: setThemeMode, setDensityMode: setThemeDensity, setAccentColor: setThemeAccentColor, setSelectedPalette } = useThemeStore();
  const [searchQuery, setSearchQuery] = useState('');
  // Track which items with children are expanded
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    GENERAL: true,
    MAIL: true,
    integrations: false,
    notification: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  // Auto-expand parent if child is active
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/settings/integrations')) {
      setExpandedItems(prev => ({ ...prev, integrations: true }));
    }
    if (path.includes('/settings/notification')) {
      setExpandedItems(prev => ({ ...prev, notification: true }));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (settings.theme) {
      const validModes: string[] = ['light', 'dark', 'system'];
      if (validModes.includes(settings.theme)) {
        setThemeMode(settings.theme as 'light' | 'dark' | 'system');
      } else {
        setSelectedPalette(settings.theme);
      }
    }
    if (settings.displayDensity) {
      setThemeDensity(settings.displayDensity as 'comfortable' | 'cozy' | 'compact');
    }
    if (settings.accentColor) {
      setThemeAccentColor(settings.accentColor as 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'teal');
    }
    syncFromBackendSettings(settings);
  }, [settings.theme, settings.displayDensity, settings.accentColor, setThemeMode, setThemeDensity, setThemeAccentColor, setSelectedPalette]);

  const settingsSections: SettingsSection[] = [
    {
      title: 'GENERAL',
      items: [
        { id: 'system', label: 'System', icon: <SystemIcon fontSize="small" />, path: '/settings/system' },
        { id: 'keyboard-shortcuts', label: 'Keyboard Shortcuts', icon: <KeyboardIcon fontSize="small" />, path: '/settings/keyboard-shortcuts' },
        {
          id: 'integrations', label: 'Integrations', icon: <ExtensionIcon fontSize="small" />, path: '/settings/integrations',
          children: [
            { id: 'integrations-extensions', label: 'Extensions', icon: <ExtensionIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/integrations/extensions' },
            { id: 'integrations-developer', label: 'Developer Space', icon: <DeveloperIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/integrations/developer' },
            { id: 'integrations-channels', label: 'Channels', icon: <ChannelsIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/integrations/channels' },
          ],
        },
        { id: 'preset', label: 'Preset', icon: <PresetIcon fontSize="small" />, path: '/settings/preset' },
        {
          id: 'notification', label: 'Notification', icon: <NotificationIcon fontSize="small" />, path: '/settings/notification',
          children: [
            { id: 'notification-desktop', label: 'Desktop Notifications', icon: <DesktopNotifIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/notification/desktop' },
            { id: 'notification-priority', label: 'Priority Users', icon: <PriorityIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/notification/priority' },
            { id: 'notification-quiet', label: 'Quiet Mode', icon: <QuietModeIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/notification/quiet' },
            { id: 'notification-mail', label: 'Mail Notifications', icon: <MailNotifIcon fontSize="small" sx={{ fontSize: 16 }} />, path: '/settings/notification/mail' },
          ],
        },
      ],
    },
    {
      title: 'MAIL',
      items: [
        { id: 'mail-view', label: 'Mail View Options', icon: <MailViewIcon fontSize="small" />, path: '/settings/mail-view' },
        { id: 'compose', label: 'Compose', icon: <ComposeIcon fontSize="small" />, path: '/settings/compose' },
        { id: 'compose-customization', label: 'Compose Customization', icon: <AppearanceIcon fontSize="small" />, path: '/settings/compose-customization' },
        { id: 'folders', label: 'Folders', icon: <FoldersIcon fontSize="small" />, path: '/settings/folders' },
        { id: 'labels', label: 'Labels', icon: <LabelsIcon fontSize="small" />, path: '/settings/labels' },
        { id: 'filters', label: 'Filters', icon: <FiltersIcon fontSize="small" />, path: '/settings/filters' },
        { id: 'out-of-office', label: 'Out of Office', icon: <OutOfOfficeIcon fontSize="small" />, path: '/settings/out-of-office' },
        { id: 'mail-accounts', label: 'Mail Accounts', icon: <MailAccountsIcon fontSize="small" />, path: '/settings/mail-accounts' },
        { id: 'signature', label: 'Signature', icon: <SignatureIcon fontSize="small" />, path: '/settings/signature' },
        { id: 'anti-spam', label: 'Blocked Senders', icon: <SpamShieldIcon fontSize="small" />, path: '/settings/anti-spam' },
        { id: 'import-export', label: 'Import / Export', icon: <ImportExportIcon fontSize="small" />, path: '/settings/import-export' },
      ],
    },
    {
      title: 'SECURITY',
      items: [
        { id: 'app-passwords', label: 'App Passwords', icon: <AppPasswordsIcon fontSize="small" />, path: '/settings/app-passwords' },
      ],
    },
  ];

  const toggleSection = (key: string) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isExactActive = (path: string) => location.pathname === path;

  const handleClose = () => navigate('/mail/inbox');

  // Flatten for search
  const allItems: SettingsMenuItem[] = [];
  settingsSections.forEach(s => {
    s.items.forEach(item => {
      allItems.push(item);
      item.children?.forEach(child => allItems.push(child));
    });
  });
  const filteredItems = searchQuery
    ? allItems.filter(i => i.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  const navItemSx = (active: boolean, indent = false) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    px: indent ? 3 : 1.5,
    py: '7px',
    mx: 1,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    background: active ? (isDark ? 'rgba(25,103,210,0.2)' : '#e8f0fe') : 'transparent',
    borderLeft: active ? '3px solid #1967d2' : '3px solid transparent',
    '&:hover': { background: active ? (isDark ? 'rgba(25,103,210,0.2)' : '#e8f0fe') : (isDark ? 'rgba(255,255,255,0.06)' : '#f1f3f4') },
  });

  const renderItem = (item: SettingsMenuItem, indent = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const expanded = expandedItems[item.id];
    const active = hasChildren ? isActive(item.path) : isExactActive(item.path);

    return (
      <Box key={item.id}>
        <Box
          sx={navItemSx(active && !hasChildren, indent)}
          onClick={() => {
            if (hasChildren) {
              toggleSection(item.id);
              navigate(item.path);
            } else {
              navigate(item.path);
            }
          }}
        >
          <Box sx={{ color: active ? '#1967d2' : (isDark ? 'rgba(255,255,255,0.6)' : '#5f6368'), display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {item.icon}
          </Box>
          <Typography sx={{
            flex: 1,
            fontSize: indent ? '0.8rem' : '0.85rem',
            fontWeight: active ? 600 : 400,
            color: active ? '#1967d2' : (isDark ? 'rgba(255,255,255,0.87)' : '#202124'),
            whiteSpace: 'nowrap',
          }}>
            {item.label}
          </Typography>
          {hasChildren && (
            <Box sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#5f6368', display: 'flex', alignItems: 'center' }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
            </Box>
          )}
        </Box>
        {hasChildren && (
          <Collapse in={expanded}>
            {item.children!.map(child => renderItem(child, true))}
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        bgcolor: 'rgba(0,0,0,0.45)',
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '94%',
          maxWidth: 1040,
          height: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Left Sidebar */}
        <Box
          sx={{
            width: 248,
            minWidth: 248,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: isDark ? '#1a1f2e' : '#f8f9fa',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 600, color: isDark ? '#fff' : '#202124', fontSize: '1.1rem', mb: 1.5 }}>
              Settings
            </Typography>
            <TextField
              fullWidth size="small" placeholder="Search settings"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.secondary' }} /></InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper', borderRadius: '6px', fontSize: '0.85rem',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: '#1967d2' },
                  '&.Mui-focused fieldset': { borderColor: '#1967d2' },
                },
                '& .MuiInputBase-input': { py: '7px' },
              }}
            />
          </Box>

          {/* Nav */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
            {filteredItems ? (
              /* Search results â€” flat list */
              filteredItems.map(item => (
                <Box key={item.id} sx={navItemSx(isExactActive(item.path))} onClick={() => navigate(item.path)}>
                  <Box sx={{ color: isExactActive(item.path) ? '#1967d2' : (isDark ? 'rgba(255,255,255,0.6)' : '#5f6368'), display: 'flex' }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: isExactActive(item.path) ? 600 : 400, color: isExactActive(item.path) ? '#1967d2' : (isDark ? 'rgba(255,255,255,0.87)' : '#202124') }}>
                    {item.label}
                  </Typography>
                </Box>
              ))
            ) : (
              settingsSections.map(section => (
                <Box key={section.title}>
                  {/* Section header */}
                  <Box
                    sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => toggleSection(section.title)}
                  >
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.45)' : '#5f6368', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                      {section.title}
                    </Typography>
                    {expandedItems[section.title]
                      ? <ExpandLessIcon sx={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.35)' : '#5f6368' }} />
                      : <ExpandMoreIcon sx={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.35)' : '#5f6368' }} />
                    }
                  </Box>
                  <Collapse in={expandedItems[section.title] !== false}>
                    {section.items.map(item => renderItem(item))}
                  </Collapse>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Right content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', minWidth: 0 }}>
          {/* Close */}
          <IconButton
            onClick={handleClose} size="small"
            sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsLayout;